import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayout from "@/components/feature/MainLayout";
import { residentUsers } from "@/mocks/users";

interface GasScene {
  id: string;
  name: string;
  icon: string;
  color: string;
  maxFlow: number;
  alertThreshold: number;
  timeRanges: string[];
  enabled: boolean;
  isCustom: boolean;
}

const defaultScenes: GasScene[] = [
  { id: "breakfast", name: "早餐模式", icon: "ri-sun-line", color: "#f59e0b", maxFlow: 2.0, alertThreshold: 3.0, timeRanges: ["06:00-09:00"], enabled: true, isCustom: false },
  { id: "lunch", name: "午餐模式", icon: "ri-restaurant-line", color: "#E8531A", maxFlow: 3.0, alertThreshold: 4.0, timeRanges: ["11:00-14:00"], enabled: true, isCustom: false },
  { id: "dinner", name: "晚餐模式", icon: "ri-moon-line", color: "#6366f1", maxFlow: 4.0, alertThreshold: 5.0, timeRanges: ["17:00-21:00"], enabled: true, isCustom: false },
  { id: "hotwater", name: "热水模式", icon: "ri-drop-line", color: "#06b6d4", maxFlow: 1.5, alertThreshold: 2.0, timeRanges: ["06:00-08:00", "20:00-23:00"], enabled: false, isCustom: false },
  { id: "heating", name: "采暖模式", icon: "ri-fire-line", color: "#ef4444", maxFlow: 8.0, alertThreshold: 10.0, timeRanges: ["00:00-24:00"], enabled: false, isCustom: false },
];

interface SceneCardProps {
  scene: GasScene;
  onToggle: (id: string) => void;
  onEdit: (scene: GasScene) => void;
  onDelete?: (id: string) => void;
}

function SceneCard({ scene, onToggle, onEdit, onDelete }: SceneCardProps) {
  return (
    <div className={`bg-white rounded-2xl border-2 p-4 transition-all ${scene.enabled ? "border-[#E8531A]/30 bg-orange-50/20" : "border-gray-100"}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${scene.color}20` }}>
            <i className={`${scene.icon} text-xl`} style={{ color: scene.color }}></i>
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">{scene.name}</p>
            {scene.isCustom && <span className="text-xs text-[#E8531A] bg-orange-50 px-1.5 py-0.5 rounded">自定义</span>}
          </div>
        </div>
        <div
          onClick={() => onToggle(scene.id)}
          className={`w-11 h-6 rounded-full cursor-pointer transition-colors relative flex-shrink-0 ${scene.enabled ? "bg-[#E8531A]" : "bg-gray-200"}`}
        >
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all shadow-sm ${scene.enabled ? "left-5" : "left-0.5"}`}></div>
        </div>
      </div>
      <div className="space-y-1.5 mb-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">最大流量</span>
          <span className="font-medium text-gray-700">{scene.maxFlow} m³/h</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">报警阈值</span>
          <span className="font-medium text-amber-600">{scene.alertThreshold} m³/h</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">启用时段</span>
          <span className="font-medium text-gray-700">{scene.timeRanges.join(", ")}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(scene)}
          className="flex-1 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-line mr-1"></i>编辑
        </button>
        {scene.isCustom && onDelete && (
          <button
            onClick={() => onDelete(scene.id)}
            className="px-3 py-1.5 text-xs border border-red-100 text-red-400 rounded-lg hover:bg-red-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        )}
      </div>
    </div>
  );
}

interface EditModalProps {
  scene: GasScene | null;
  onClose: () => void;
  onSave: (scene: GasScene) => void;
}

function EditModal({ scene, onClose, onSave }: EditModalProps) {
  const [form, setForm] = useState<GasScene>(
    scene ?? {
      id: `custom_${Date.now()}`,
      name: "",
      icon: "ri-settings-3-line",
      color: "#E8531A",
      maxFlow: 2.0,
      alertThreshold: 3.0,
      timeRanges: ["08:00-10:00"],
      enabled: true,
      isCustom: true,
    }
  );

  const iconOptions = [
    "ri-sun-line", "ri-moon-line", "ri-restaurant-line", "ri-fire-line",
    "ri-drop-line", "ri-home-4-line", "ri-settings-3-line", "ri-star-line",
  ];
  const colorOptions = ["#E8531A", "#f59e0b", "#10b981", "#6366f1", "#ef4444", "#06b6d4", "#8b5cf6", "#ec4899"];

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-[520px] max-w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-bold text-gray-900">{scene ? "编辑场景" : "新建自定义场景"}</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">场景名称</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="如：周末聚餐模式"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
            />
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-2 block">场景图标</label>
            <div className="flex gap-2 flex-wrap">
              {iconOptions.map(icon => (
                <button
                  key={icon}
                  onClick={() => setForm(f => ({ ...f, icon }))}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg border-2 cursor-pointer transition-colors ${form.icon === icon ? "border-[#E8531A] bg-orange-50" : "border-gray-100"}`}
                >
                  <i className={`${icon} text-base ${form.icon === icon ? "text-[#E8531A]" : "text-gray-400"}`}></i>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-2 block">主题颜色</label>
            <div className="flex gap-2">
              {colorOptions.map(color => (
                <button
                  key={color}
                  onClick={() => setForm(f => ({ ...f, color }))}
                  className={`w-7 h-7 rounded-full cursor-pointer border-2 transition-all ${form.color === color ? "border-gray-400 scale-110" : "border-transparent"}`}
                  style={{ backgroundColor: color }}
                ></button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">最大流量 (m³/h)</label>
              <input
                type="number"
                value={form.maxFlow}
                onChange={e => setForm(f => ({ ...f, maxFlow: Number(e.target.value) }))}
                step="0.5"
                min="0.5"
                max="20"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">报警阈值 (m³/h)</label>
              <input
                type="number"
                value={form.alertThreshold}
                onChange={e => setForm(f => ({ ...f, alertThreshold: Number(e.target.value) }))}
                step="0.5"
                min="0.5"
                max="25"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">启用时段（格式：HH:MM-HH:MM）</label>
            {form.timeRanges.map((tr, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <input
                  value={tr}
                  onChange={e => {
                    const newRanges = [...form.timeRanges];
                    newRanges[idx] = e.target.value;
                    setForm(f => ({ ...f, timeRanges: newRanges }));
                  }}
                  placeholder="06:00-09:00"
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
                />
                {form.timeRanges.length > 1 && (
                  <button
                    onClick={() => setForm(f => ({ ...f, timeRanges: f.timeRanges.filter((_, i) => i !== idx) }))}
                    className="w-8 h-8 flex items-center justify-center text-red-400 hover:bg-red-50 rounded-lg cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                )}
              </div>
            ))}
            {form.timeRanges.length < 3 && (
              <button
                onClick={() => setForm(f => ({ ...f, timeRanges: [...f.timeRanges, ""] }))}
                className="text-xs text-[#E8531A] hover:underline cursor-pointer"
              >
                + 添加时段
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-sm text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
          <button
            onClick={() => { if (form.name.trim()) { onSave(form); onClose(); } }}
            disabled={!form.name.trim()}
            className="flex-1 py-2.5 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] disabled:opacity-40 cursor-pointer whitespace-nowrap"
          >
            保存场景
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ResidentScenePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = residentUsers.find(u => u.id === id);
  const [scenes, setScenes] = useState<GasScene[]>(defaultScenes);
  const [editingScene, setEditingScene] = useState<GasScene | null | "new">(null);

  const toggleScene = (sceneId: string) => {
    setScenes(prev => prev.map(s => s.id === sceneId ? { ...s, enabled: !s.enabled } : s));
  };

  const saveScene = (scene: GasScene) => {
    setScenes(prev => {
      const exists = prev.find(s => s.id === scene.id);
      return exists ? prev.map(s => s.id === scene.id ? scene : s) : [...prev, scene];
    });
  };

  const deleteScene = (sceneId: string) => {
    setScenes(prev => prev.filter(s => s.id !== sceneId));
  };

  const activeScenes = scenes.filter(s => s.enabled);

  return (
    <MainLayout>
      <div className="space-y-4">
        <button onClick={() => navigate(`/profile/${id}`)} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 cursor-pointer">
          <i className="ri-arrow-left-line"></i>返回用户详情
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">用气场景管理</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {user ? `${user.name} · ` : ""}自定义用气场景，设置流量阈值与报警规则
            </p>
          </div>
          <button
            onClick={() => setEditingScene("new")}
            className="flex items-center gap-2 px-4 py-2 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>新建场景
          </button>
        </div>

        {/* Active Summary */}
        <div className="bg-gradient-to-r from-[#E8531A] to-[#f97316] rounded-2xl p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-bold">当前激活场景</p>
              <p className="text-xs text-white/70 mt-0.5">共 {activeScenes.length} 个场景已启用</p>
            </div>
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-settings-3-line text-white text-xl"></i>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {activeScenes.length === 0 ? (
              <span className="text-xs text-white/60">暂无激活场景</span>
            ) : (
              activeScenes.map(s => (
                <div key={s.id} className="flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1">
                  <i className={`${s.icon} text-xs`}></i>
                  <span className="text-xs font-medium">{s.name}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Usage History Comparison */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">历史用气 vs 当前数据对比</h3>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {[
              { label: "本月用气", current: user?.monthlyUsage ?? 28.5, prev: 32.1, unit: "m³" },
              { label: "年度累计", current: user?.yearlyUsage ?? 342, prev: 356, unit: "m³" },
              { label: "日均用气", current: ((user?.monthlyUsage ?? 28.5) / 30).toFixed(1), prev: "1.07", unit: "m³/天" },
              { label: "当前余额", current: `¥${user?.balance ?? 256}`, prev: "¥320", unit: "" },
            ].map(item => (
              <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                <p className="text-base font-bold text-gray-900">{item.current}{item.unit && <span className="text-xs font-normal text-gray-400 ml-1">{item.unit}</span>}</p>
                <p className="text-xs text-gray-400 mt-0.5">上期：{item.prev}{item.unit}</p>
              </div>
            ))}
          </div>

          {/* Mini usage chart */}
          {user && (
            <div>
              <p className="text-xs text-gray-400 mb-2">近12个月用气趋势</p>
              <div className="flex items-end gap-1 h-16">
                {user.usageHistory.map((h, i) => {
                  const maxUsage = Math.max(...user.usageHistory.map(x => x.usage));
                  const pct = (h.usage / maxUsage) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full rounded-t-sm bg-[#E8531A]/70 transition-all"
                        style={{ height: `${pct}%` }}
                        title={`${h.month}: ${h.usage}m³`}
                      ></div>
                      <span className="text-xs text-gray-300" style={{ fontSize: "8px" }}>{h.month.slice(5)}月</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Scene Grid */}
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-3">全部场景</h3>
          <div className="grid grid-cols-3 gap-3">
            {scenes.map(scene => (
              <SceneCard
                key={scene.id}
                scene={scene}
                onToggle={toggleScene}
                onEdit={s => setEditingScene(s)}
                onDelete={deleteScene}
              />
            ))}
          </div>
        </div>

        {/* Alarm Threshold Settings */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">用气习惯及阈值报警设置</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-2">用气习惯分析</p>
              <div className="space-y-2">
                {(user?.portrait.habits ?? ["早餐用气", "晚餐用气量大"]).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#E8531A] flex-shrink-0"></div>
                    <span className="text-gray-600">{h}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-2">高峰用气时段</p>
              <div className="space-y-1.5">
                {(user?.portrait.peakHours ?? "07:00-08:30, 18:00-20:00").split(", ").map((t, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-lg">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-[#E8531A] text-xs"></i>
                    </div>
                    <span className="text-xs font-medium text-gray-700">{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {editingScene !== null && (
        <EditModal
          scene={editingScene === "new" ? null : editingScene}
          onClose={() => setEditingScene(null)}
          onSave={saveScene}
        />
      )}
    </MainLayout>
  );
}
